import { Box, Spinner, Text } from "@primer/react";
import { useEffect, useRef } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, AuthProviderProps, useAuth as useOidcAuth } from "oidc-react";
import { appConst } from "../../AppConst";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAuth } from "../../hooks/useAuth";
import { rootActions } from "../../state/actions/rootActions";
import { eReducerStatusBase } from "../../state/reducer-model/eReducerStatusBase";

/** Dọn sessionStorage PKCE/state của luồng VNPT (gọi khi xong hoặc lỗi để tránh stale verifier). */
const clearVnptSessionState = () => {
    sessionStorage.removeItem("login_vnpt_code");
    sessionStorage.removeItem("code_verifier");
    sessionStorage.removeItem("oauth_state");
};

/** Loader full-page dùng chung trong lúc redirect / trao đổi token với cổng SSO. */
const SsoLoader = ({ text = "Đang chuyển tới cổng đăng nhập..." }: { text?: string }) => (
    <Box
        sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            background: "linear-gradient(135deg, #e8eefc 0%, #eef0f7 45%, #e7ecf7 100%)",
        }}
    >
        <Spinner size="large" />
        <Text sx={{ color: "#6a73a0", fontWeight: 600 }}>{text}</Text>
    </Box>
);

/** redirect_uri callback — ưu tiên env (phải khớp giá trị đã đăng ký ở IdP), fallback origin runtime. */
const resolveRedirectUri = () =>
    appConst.sso.redirectUri || `${window.location.origin}/signin-oidc`;

// ── VNPT: Authorization Code + PKCE thủ công (mirror MARKMAN UnAuthorizedContentVNPT) ──────────

const pkceChallengeFromVerifier = async (verifier: string) => {
    const data = new TextEncoder().encode(verifier);
    const digest = await window.crypto.subtle.digest("SHA-256", data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
};

const generateRandomString = (length = 64) => {
    const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
    const values = new Uint32Array(length);
    window.crypto.getRandomValues(values);
    let result = "";
    for (let i = 0; i < length; i++) result += charset.charAt(values[i] % charset.length);
    return result;
};

const VnptSignIn = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const status = useAppSelector(s => s.auth.status);
    const attemptedRef = useRef(false);
    const query = new URLSearchParams(location.search);
    const code = query.get("code");
    const state = query.get("state");
    const session_state = query.get("session_state") ?? "";

    // Login SSO lỗi (mã SV không tồn tại / đợt chưa mở / token sai) → quay về /login để hiện
    // thông báo lỗi, tránh kẹt vĩnh viễn ở spinner. Dọn luôn state PKCE để lần sau lấy code mới.
    // CHỈ khi mount này thực sự đã gọi login (attemptedRef) — tránh bounce do is_error cũ còn trong store.
    useEffect(() => {
        if (attemptedRef.current && status === eReducerStatusBase.is_error) {
            clearVnptSessionState();
            navigate("/login", { replace: true });
        }
    }, [status, navigate]);

    useEffect(() => {
        if (user) {
            // Login xong: dọn code_verifier + login_vnpt_code; GIỮ oauth_state để truyền `state`
            // vào end-session URL khi logout (giống MARKMAN). oauth_state ghi đè ở lần login kế.
            sessionStorage.removeItem("code_verifier");
            sessionStorage.removeItem("login_vnpt_code");
            navigate("/", { replace: true });
            return;
        }
        const run = async () => {
            const code_verifier = sessionStorage.getItem("code_verifier") ?? "";
            if (code && code_verifier) {
                // Code OIDC chỉ dùng được 1 lần. Nếu đã gửi rồi (component remount sau login lỗi) →
                // dọn dẹp + quay lại /login để lấy code mới, tránh lỗi "code đã sử dụng".
                if (sessionStorage.getItem("login_vnpt_code") === code) {
                    clearVnptSessionState();
                    navigate("/login", { replace: true });
                    return;
                }
                // Chống CSRF/replay: state trả về phải khớp state đã sinh lúc redirect authorize.
                // Lệch / thiếu → từ chối, dọn state, hiện lỗi ở /login (không gửi code lên backend).
                const savedState = sessionStorage.getItem("oauth_state");
                if (!state || !savedState || state !== savedState) {
                    clearVnptSessionState();
                    navigate(
                        `/login?oauth_error=${encodeURIComponent("Phiên đăng nhập không hợp lệ hoặc đã hết hạn. Vui lòng thử lại.")}`,
                        { replace: true }
                    );
                    return;
                }
                sessionStorage.setItem("login_vnpt_code", code);
                attemptedRef.current = true;
                dispatch(
                    rootActions.auth.LOGIN_VNPT_START({
                        code,
                        code_verifier,
                        redirect_uri: resolveRedirectUri(),
                        state: state ?? "",
                        session_state,
                    })
                );
            } else {
                // Lần đầu: sinh PKCE + redirect tới authorize endpoint VNPT.
                const newState = generateRandomString();
                const codeVerifier = generateRandomString(64);
                const codeChallenge = await pkceChallengeFromVerifier(codeVerifier);
                sessionStorage.setItem("code_verifier", codeVerifier);
                sessionStorage.setItem("oauth_state", newState);
                const authUrl =
                    `${appConst.sso.authority}?response_type=code` +
                    `&client_id=${encodeURIComponent(appConst.sso.clientId)}` +
                    `&redirect_uri=${encodeURIComponent(resolveRedirectUri())}` +
                    `&scope=${encodeURIComponent(appConst.sso.scope)}` +
                    `&state=${newState}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
                window.location.href = authUrl;
            }
        };
        run();
    }, [code, state, session_state, user, dispatch, navigate]);

    return <SsoLoader text="Đang xác thực với cổng VNPT..." />;
};

// ── Nam Việt / ESSOFT: OIDC chuẩn qua oidc-react (mirror MARKMAN UnAuthorizedContent) ──────────

const NamVietInner = () => {
    const auth = useOidcAuth();
    const dispatch = useAppDispatch();
    const { user } = useAuth();
    const status = useAppSelector(s => s.auth.status);
    const navigate = useNavigate();
    const sentRef = useRef(false);

    const accessToken = auth.userData?.access_token ?? "";

    // Login lỗi → về /login (hiện thông báo) thay vì kẹt spinner. Chỉ khi mount này đã gọi login
    // (sentRef) để tránh bounce do is_error cũ còn trong store.
    useEffect(() => {
        if (sentRef.current && status === eReducerStatusBase.is_error) navigate("/login", { replace: true });
    }, [status, navigate]);

    useEffect(() => {
        if (user) {
            navigate("/", { replace: true });
            return;
        }
        if (accessToken && !sentRef.current) {
            sentRef.current = true;
            dispatch(rootActions.auth.LOGIN_NAMVIET_START({ access_token: accessToken }));
        }
    }, [accessToken, user, dispatch, navigate]);

    return <SsoLoader text="Đang xác thực với cổng đăng nhập..." />;
};

/**
 * Cấu hình oidc-react cho Nam Việt. autoSignIn=true (mặc định) cho luồng đăng nhập (tự redirect
 * tới authority + xử lý callback ?code=). autoSignIn=false cho luồng đăng xuất (tránh tự đăng nhập
 * lại trước khi kịp gọi signOutRedirect).
 */
const buildNamVietOidcConfig = (autoSignIn = true): AuthProviderProps => ({
    authority: appConst.sso.authority,
    clientId: appConst.sso.clientId,
    clientSecret: appConst.sso.clientSecret || undefined,
    redirectUri: resolveRedirectUri(),
    postLogoutRedirectUri: window.location.origin,
    responseType: "code",
    scope: appConst.sso.scope,
    automaticSilentRenew: true,
    autoSignIn,
    onSignIn: () => {
        /* token nhận qua userData; LOGIN_NAMVIET_START dispatch trong NamVietInner */
    },
});

const NamVietSignIn = () => (
    <AuthProvider {...buildNamVietOidcConfig()}>
        <NamVietInner />
    </AuthProvider>
);

/** Gọi RP-initiated logout tới end-session endpoint của Nam Việt (kết thúc phiên IdP + xoá storage oidc-client). */
const NamVietSignOutInner = () => {
    const auth = useOidcAuth();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const ranRef = useRef(false);
    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;
        // Clear phiên local TRƯỚC (an toàn ở route public này) — nếu không, khi IdP redirect về
        // origin, SessionRestorer sẽ tự khôi phục lại đăng nhập từ localStorage.
        dispatch(rootActions.auth.LOGOUT());
        auth
            .signOutRedirect({ post_logout_redirect_uri: window.location.origin })
            .catch(() => navigate("/login", { replace: true }));
    }, [auth, dispatch, navigate]);
    return <SsoLoader text="Đang đăng xuất khỏi cổng đăng nhập..." />;
};

/**
 * Đăng xuất VNPT — build end-session URL giống MARKMAN (id_token_hint + post_logout_redirect_uri +
 * state) rồi window.location.replace. Đọc sso_id_token/oauth_state TRƯỚC khi dispatch LOGOUT (LOGOUT
 * xoá sso_id_token). id_token_hint lấy từ sso_id_token (id_token VNPT) vì access_token AOF là JWT nội bộ.
 */
const VnptSignOut = () => {
    const dispatch = useAppDispatch();
    const ranRef = useRef(false);
    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;
        const idTokenHint = sessionStorage.getItem("sso_id_token") ?? "";
        const oauthState = sessionStorage.getItem("oauth_state") ?? "";
        dispatch(rootActions.auth.LOGOUT());
        if (appConst.sso.logout) {
            const url = new URL(appConst.sso.logout);
            if (idTokenHint) url.searchParams.append("id_token_hint", idTokenHint);
            if (appConst.sso.redirectUri)
                url.searchParams.append("post_logout_redirect_uri", appConst.sso.redirectUri);
            if (oauthState) url.searchParams.append("state", oauthState);
            window.location.replace(url.toString());
        } else {
            window.location.replace("/login");
        }
    }, [dispatch]);
    return <SsoLoader text="Đang đăng xuất khỏi cổng SSO..." />;
};

/**
 * Trang /signout-oidc — đăng xuất triệt để khỏi cổng SSO (route public, ngoài ProtectedRoute → clear
 * token tại đây không bị đá về /login).
 *   namviet → oidc-react signOutRedirect (kèm id_token_hint tự lấy từ userData).
 *   vnpt    → end-session URL VNPT + window.location.replace.
 *   none    → về /login.
 */
export const SsoSignOutPage = () => {
    const unit = appConst.sso.unit;
    if (unit === "namviet") {
        return (
            <AuthProvider {...buildNamVietOidcConfig(false)}>
                <NamVietSignOutInner />
            </AuthProvider>
        );
    }
    if (unit === "vnpt") return <VnptSignOut />;
    return <Navigate to="/login" replace />;
};

/**
 * Trang xử lý đăng nhập SSO tại route /signin-oidc — chọn luồng theo REACT_APP_OIDC_UNIT:
 *   "vnpt"    → VnptSignIn (PKCE thủ công)
 *   "namviet" → NamVietSignIn (oidc-react)
 * Unit khác (không bật SSO) → về /login.
 */
const SsoSignInPage = () => {
    const navigate = useNavigate();
    const unit = appConst.sso.unit;

    useEffect(() => {
        if (unit !== "vnpt" && unit !== "namviet") navigate("/login", { replace: true });
    }, [unit, navigate]);

    if (unit === "vnpt") return <VnptSignIn />;
    if (unit === "namviet") return <NamVietSignIn />;
    return <SsoLoader text="Đang chuyển hướng..." />;
};

export default SsoSignInPage;
